import React, { useState, useCallback } from 'react';
import { Box, Switch, CircularProgress } from '@mui/material';
import {
  ModerationPaper,
  EmailsHelperText,
  ActionsContainer,
  UpdateButton,
  EmailsTextField,
  PageSectionTitle,
  ModerationStatusLabel,
  ModerationDisabledForLabel
} from './styled';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import { PaperWrapper, Title } from '../common/styled';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import useModerationConfig from './hooks/useModerationConfig';

const SchoolConfigurationsPage: React.FC = () => {
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );
  const school = loggedInUser?.school || '';
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: SnackbarSeverityType.success as SnackbarSeverityType
  });

  const showToast = useCallback(
    (message: string, severity: SnackbarSeverityType) => {
      setToast({ open: true, message, severity });
    },
    []
  );

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHookSettings((prev) => ({
      ...prev,
      postModerationEnabled: event.target.checked
    }));
  };

  const handleEmailsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHookSettings((prev) => ({
      ...prev,
      disabledEmailsCsv: event.target.value
    }));
  };

  const {
    settings: hookSettings,
    setSettings: setHookSettings,
    loading: initialLoading,
    saving,
    update
  } = useModerationConfig({ school, showToast });
  const settings = hookSettings;

  const handleUpdate = async () => {
    await update();
  };

  const moderationLabel = `Post Moderation (Currently ${
    settings.postModerationEnabled ? 'Enabled' : 'Disabled'
  })`;

  return (
    <>
      <Title>School Configurations</Title>
      <PaperWrapper>
        <ModerationPaper>
          <PageSectionTitle>Moderation</PageSectionTitle>
          <Box display='flex' alignItems='center' gap={2}>
            <ModerationStatusLabel>{moderationLabel}</ModerationStatusLabel>
            <Switch
              checked={settings.postModerationEnabled}
              onChange={handleToggleChange}
              disabled={initialLoading}
            />
          </Box>
          <Box
            display='flex'
            flexDirection='row'
            gap={2}
            sx={{
              '@media (max-width: 1274px)': { flexDirection: 'row', gap: 1 }
            }}
          >
            <ModerationDisabledForLabel>
              Moderation Explicitly Disabled For
            </ModerationDisabledForLabel>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                flex: 1
              }}
            >
              <EmailsTextField
                value={settings.disabledEmailsCsv}
                onChange={handleEmailsChange}
                disabled={initialLoading}
                placeholder='Enter email addresses'
                multiline
              />
              <EmailsHelperText>
                Enter email ids of users (registered in ether)
                <br /> separated by a comma
              </EmailsHelperText>
            </Box>
          </Box>
          <ActionsContainer>
            <UpdateButton
              variant='contained'
              disabled={saving || initialLoading}
              onClick={handleUpdate}
            >
              {saving || initialLoading ? (
                <CircularProgress size={20} />
              ) : (
                'Update'
              )}
            </UpdateButton>
          </ActionsContainer>
        </ModerationPaper>
        <ToastMessage
          open={toast.open}
          message={toast.message}
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        />
      </PaperWrapper>
    </>
  );
};

export default SchoolConfigurationsPage;
